import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Car, Package, Mail, CheckCircle2, ImageIcon, Printer, ThumbsUp, ThumbsDown } from "lucide-react";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/apiClient";
import { URGENCY, STATUS } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const esc = (val) => String(val ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1.5 text-sm border-b border-border/60 last:border-0">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium text-right">{value || "—"}</span>
  </div>
);

export default function RequestDetailDialog({ req, isAdmin, open, onOpenChange, onUpdated }) {
  const [quote, setQuote] = useState({ price_ht: "", price_ttc: "", availability: "", delay_days: "", message: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && req && (req.notify_garage || req.notify_admin)) {
      api.patch(`/requests/${req.id}/seen`)
        .then(() => onUpdated?.({ ...req, notify_garage: false, notify_admin: false }))
        .catch(() => {});
    }
  }, [open, req, onUpdated]);

  if (!req) return null;

  const u = URGENCY[req.urgency] || URGENCY.stock;
  const s = STATUS[req.status] || STATUS.pending;
  const v = req.vehicle || {};

  const submitQuote = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/requests/${req.id}/quote`, {
        price_ht: quote.price_ht ? parseFloat(quote.price_ht) : null,
        price_ttc: quote.price_ttc ? parseFloat(quote.price_ttc) : null,
        availability: quote.availability,
        delay_days: quote.delay_days ? parseInt(quote.delay_days) : null,
        message: quote.message,
      });
      toast.success("Devis enregistré");
      onUpdated?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const changeStatus = async (status) => {
    try {
      const { data } = await api.patch(`/requests/${req.id}/status`, { status });
      toast.success("Statut mis à jour");
      onUpdated?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const decide = async (decision) => {
    try {
      const { data } = await api.post(`/requests/${req.id}/decision`, { decision });
      toast.success(decision === "accepted" ? "Devis accepté — fournisseur notifié" : "Devis refusé — fournisseur notifié");
      onUpdated?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const printPdf = () => {
    const q = req.quote || {};
    const dec = req.decision || {};
    const win = window.open("", "_blank");
    if (!win) { toast.error("Autorisez les pop-ups pour l'export PDF"); return; }
    const line = (l, val) => val ? `<tr><td style="padding:6px 10px;border:1px solid #e2e8f0;color:#64748b">${l}</td><td style="padding:6px 10px;border:1px solid #e2e8f0;font-weight:600">${esc(val)}</td></tr>` : "";
    win.document.write(`<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Demande ${esc(req.part_name)}</title>
      <style>body{font-family:Arial,Helvetica,sans-serif;color:#0f172a;max-width:720px;margin:24px auto;padding:0 16px}
      h1{color:#0033cc;font-size:22px;margin-bottom:2px}.sub{color:#64748b;margin-top:0}
      .brand{display:flex;align-items:center;gap:10px;border-bottom:3px solid #dc2626;padding-bottom:10px;margin-bottom:16px}
      .logo{width:56px;height:56px;object-fit:contain}
      table{width:100%;border-collapse:collapse;margin:10px 0 20px}h2{font-size:15px;margin:18px 0 6px;color:#1e40af}
      .badge{display:inline-block;padding:4px 10px;border-radius:999px;font-size:12px;font-weight:700;background:#fee2e2;color:#dc2626}
      .foot{color:#94a3b8;font-size:12px;margin-top:30px;border-top:1px solid #e2e8f0;padding-top:10px}</style></head>
      <body onload="window.print()">
      <div class="brand"><img class="logo" src="${window.location.origin}/logo.png" alt="" /><div><h1>Méca Pièces <span style="color:#dc2626">PRO</span></h1><p class="sub">Bon de demande de pièce</p></div></div>
      <span class="badge">${(URGENCY[req.urgency]||URGENCY.stock).short}${req.urgency==="delay"&&req.delay_days?" "+req.delay_days+"j":""}</span>
      <span class="badge" style="background:#dbeafe;color:#1e40af">${(STATUS[req.status]||STATUS.pending).label}</span>
      <h2>Véhicule</h2><table>
      ${line("Identifiant", `${v.input_value} (${v.input_type==="vin"?"VIN":"Plaque"})`)}
      ${line("Marque / Modèle", `${v.marque||""} ${v.modele||""}`)}
      ${line("Année", v.annee)}${line("Motorisation", `${v.motorisation||""} ${v.carburant||""}`)}${line("Puissance", v.puissance)}</table>
      <h2>Pièce demandée</h2><table>
      ${line("Nom", req.part_name)}${line("Référence OEM", req.part_reference)}
      ${line("Garage", req.garage_name||req.created_by_name)}${line("Notes", req.notes)}</table>
      ${req.quote?`<h2>Devis fournisseur</h2><table>
      ${line("Prix HT", q.price_ht!=null?q.price_ht+" €":"")}${line("Prix TTC", q.price_ttc!=null?q.price_ttc+" €":"")}
      ${line("Disponibilité", q.availability)}${line("Délai", q.delay_days?q.delay_days+" j":"")}${line("Message", q.message)}</table>`:""}
      ${dec.value?`<h2>Décision du garage</h2><p><strong>${dec.value==="accepted"?"Devis ACCEPTÉ":"Devis REFUSÉ"}</strong> ${esc(dec.message)}</p>`:""}
      <div class="foot">Édité le ${new Date().toLocaleString("fr-FR")} — Méca Pièces PRO</div>
      </body></html>`);
    win.document.close();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="request-detail-view" className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Package className="h-5 w-5 text-blue-600" /> {req.part_name}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap gap-2">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${u.badge}`}>{u.short}{req.urgency === "delay" && req.delay_days ? ` ${req.delay_days}j` : ""}</span>
          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${s.cls}`}>{s.label}</span>
          {req.email_sent && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-600"><Mail className="h-3.5 w-3.5" />Email envoyé</span>}
        </div>

        <div className="rounded-xl border border-border p-4 mt-2">
          <div className="flex items-center gap-2 font-semibold mb-2"><Car className="h-4 w-4 text-blue-600" />Véhicule</div>
          <Row label="Identifiant" value={`${v.input_value} (${v.input_type === "vin" ? "VIN" : "Plaque"})`} />
          <Row label="Marque / Modèle" value={`${v.marque} ${v.modele}`} />
          <Row label="Année" value={v.annee} />
          <Row label="Motorisation" value={`${v.motorisation || ""} ${v.carburant || ""}`} />
          <Row label="Puissance" value={v.puissance} />
        </div>

        <div className="rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 font-semibold mb-2"><Package className="h-4 w-4 text-blue-600" />Pièce</div>
          <Row label="Nom" value={req.part_name} />
          <Row label="Référence OEM" value={req.part_reference} />
          <Row label="Garage" value={req.garage_name || req.created_by_name} />
          {req.notes && <div className="text-sm text-muted-foreground mt-2 italic">« {req.notes} »</div>}
        </div>

        {req.photos && req.photos.length > 0 && (
          <div className="rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 font-semibold mb-2"><ImageIcon className="h-4 w-4 text-blue-600" />Photos ({req.photos.length})</div>
            <div className="grid grid-cols-3 gap-2">
              {req.photos.map((p, i) => (
                <a key={p.path} href={fileUrl(p.path)} target="_blank" rel="noreferrer" data-testid={`photo-${i}`} className="block h-24 rounded-lg overflow-hidden border border-border">
                  <img src={fileUrl(p.path)} alt={p.filename} className="h-full w-full object-cover hover:scale-105 transition-transform" />
                </a>
              ))}
            </div>
          </div>
        )}

        {req.quote && (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
            <div className="font-semibold mb-2 text-emerald-700">Devis fournisseur</div>
            <Row label="Prix HT" value={req.quote.price_ht != null ? `${req.quote.price_ht} €` : null} />
            <Row label="Prix TTC" value={req.quote.price_ttc != null ? `${req.quote.price_ttc} €` : null} />
            <Row label="Disponibilité" value={req.quote.availability} />
            <Row label="Délai" value={req.quote.delay_days ? `${req.quote.delay_days} j` : null} />
            {req.quote.message && <div className="text-sm mt-1">{req.quote.message}</div>}
            {req.decision?.value && (
              <div className={`mt-3 text-sm font-semibold ${req.decision.value === "accepted" ? "text-emerald-700" : "text-red-600"}`} data-testid="decision-status">
                {req.decision.value === "accepted" ? "✓ Devis accepté par le garage" : "✕ Devis refusé par le garage"}
              </div>
            )}
          </div>
        )}

        {isAdmin && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 space-y-3" data-testid="admin-panel">
            <div className="font-semibold text-red-700">Espace administrateur — Répondre</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Prix HT (€)</Label><Input data-testid="quote-ht" type="number" value={quote.price_ht} onChange={(e) => setQuote({ ...quote, price_ht: e.target.value })} className="mt-1 h-9" /></div>
              <div><Label className="text-xs">Prix TTC (€)</Label><Input data-testid="quote-ttc" type="number" value={quote.price_ttc} onChange={(e) => setQuote({ ...quote, price_ttc: e.target.value })} className="mt-1 h-9" /></div>
              <div><Label className="text-xs">Disponibilité</Label><Input data-testid="quote-avail" value={quote.availability} onChange={(e) => setQuote({ ...quote, availability: e.target.value })} placeholder="En stock" className="mt-1 h-9" /></div>
              <div><Label className="text-xs">Délai (jours)</Label><Input data-testid="quote-delay" type="number" value={quote.delay_days} onChange={(e) => setQuote({ ...quote, delay_days: e.target.value })} className="mt-1 h-9" /></div>
            </div>
            <Textarea data-testid="quote-message" value={quote.message} onChange={(e) => setQuote({ ...quote, message: e.target.value })} placeholder="Message au garage..." />
            <Button data-testid="submit-quote-btn" onClick={submitQuote} disabled={saving} className="w-full bg-blue-700 hover:bg-blue-800">Envoyer le devis</Button>
          </div>
        )}

        {/* Garagiste : décision sur le devis */}
        {!isAdmin && req.quote && req.status === "quoted" && !req.decision?.value && (
          <div className="flex gap-2 pt-1" data-testid="decision-actions">
            <Button data-testid="accept-quote-btn" onClick={() => decide("accepted")} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
              <ThumbsUp className="h-4 w-4 mr-2" />Accepter le devis
            </Button>
            <Button data-testid="refuse-quote-btn" onClick={() => decide("refused")} variant="outline" className="flex-1 border-red-500/40 text-red-600 hover:bg-red-500/5">
              <ThumbsDown className="h-4 w-4 mr-2" />Refuser
            </Button>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          <Button data-testid="pdf-btn" onClick={printPdf} variant="outline" className="flex-1">
            <Printer className="h-4 w-4 mr-2" />Imprimer / PDF
          </Button>
          {isAdmin && req.status !== "processed" && (
            <Button data-testid="process-btn" onClick={() => changeStatus("processed")} variant="outline" className="flex-1">
              <CheckCircle2 className="h-4 w-4 mr-2" />Marquer traité
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
